// Helpers shared by the Owner Console sections.

import { ownerHeader } from '../../utils/ownerSession';
import { actAsHeader } from '../../utils/actAs';

export const API = process.env.REACT_APP_API_URL;

// Portal JWT plus, once the owner has unlocked, the X-Owner-Token the console routes want
// (and X-WK-Act-As while a White Knight owner is acting as a partner company)
export const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  ...ownerHeader(),
  ...actAsHeader()
});

export const money = (cents) => (cents == null ? '—' : `$${(cents / 100).toFixed(2)}`);
export const dollars = (n) => (n == null || n === '' || Number.isNaN(Number(n)) ? '—' : `$${Number(n).toFixed(2)}`);
export const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');
export const fmtDay = (d) => (d ? new Date(d).toLocaleDateString() : '—');

export const numField = (props) => ({
  type: 'number',
  size: 'small',
  fullWidth: true,
  ...props
});

// Static fallback for config.vehicleTypes (mirrors backend/models/CustomerAppConfig.js)
export const VEHICLE_TYPES = [
  { key: 'car', label: 'Car', class: 'light', family: 'auto' },
  { key: 'suv', label: 'SUV', class: 'light', family: 'auto' },
  { key: 'pickup', label: 'Pickup', class: 'light', family: 'auto' },
  { key: 'van', label: 'Van', class: 'light', family: 'auto' },
  { key: 'motorcycle', label: 'Motorcycle', class: 'light', family: 'auto' },
  { key: 'travel_trailer', label: 'Travel trailer', class: 'medium', family: 'rv' },
  { key: 'popup', label: 'Pop-up camper', class: 'medium', family: 'rv' },
  { key: 'utility_trailer', label: 'Utility trailer', class: 'medium', family: 'rv' },
  { key: 'fifth_wheel', label: 'Fifth wheel', class: 'heavy', family: 'rv' },
  { key: 'motorhome', label: 'Motorhome', class: 'heavy', family: 'rv' },
  { key: 'toy_hauler', label: 'Toy hauler', class: 'heavy', family: 'rv' }
];

export const vehicleTypesFrom = (config) => (
  Array.isArray(config?.vehicleTypes) && config.vehicleTypes.length ? config.vehicleTypes : VEHICLE_TYPES
);

export const vehicleTypeLabel = (config, key) => (
  vehicleTypesFrom(config).find((t) => t.key === key)?.label || (key || 'Car')
);

export const PLAN_FAMILIES = [
  { key: 'auto', name: 'Auto', planKeys: ['auto_basic', 'auto_plus', 'auto_premium'] },
  { key: 'rv', name: 'RV', planKeys: ['rv_standard', 'rv_platinum', 'rv_complete'] }
];
export const PLAN_KEYS = PLAN_FAMILIES.flatMap((f) => f.planKeys);

export const planName = (config, planKey) => {
  if (!planKey) return null;
  return (config?.plans || []).find((p) => p.key === planKey)?.name || planKey;
};

export const intervalLabel = (interval) => {
  if (interval === 'month') return 'Monthly';
  if (interval === 'year') return 'Annual';
  return '—';
};

// Columns the backend derives when the owner leaves a class blank
export const CLASS_MULTIPLIERS = { light: 1, medium: 1.6, heavy: 2.5 };

export const round2 = (n) => +(Number(n || 0)).toFixed(2);

export const isBlank = (v) => v === undefined || v === null || v === '';

/**
 * Open a protected file in a new tab: fetch it with the bearer token, turn
 * it into a blob URL and hand that to window.open.
 */
export async function openAuthenticatedFile(url) {
  const res = await fetch(url, { headers: getAuthHeader() });
  if (!res.ok) {
    let message = `Failed to open file (${res.status})`;
    try {
      const body = await res.json();
      if (body?.message) message = body.message;
    } catch {
      // not JSON, keep the default message
    }
    throw new Error(message);
  }
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  // No 'noopener' feature here: it makes window.open return null, which we need to detect a blocked pop-up.
  // The blob URL is same-origin and carries no opener risk.
  const win = window.open(blobUrl, '_blank');
  // Give the new tab time to load the blob before it is released
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60 * 1000);
  if (!win) throw new Error('Pop-up blocked — allow pop-ups for this site to view receipts');
}
